const types = [
  {
    colour: 'Green',
    badge: 'Green Hydrogen',
    badgeStyle: 'bg-primary-100 text-primary-700',
    borderStyle: 'border-primary-200',
    icon: 'ri-sun-line',
    iconBg: 'bg-primary-50',
    iconColor: 'text-primary-600',
    tagline: 'The gold standard — zero emissions from production to use',
    description: "Green hydrogen is produced by splitting water (H₂O) into hydrogen and oxygen using renewable electricity — a process called electrolysis. Because the electricity comes from solar, wind, or other renewables, the entire production process emits zero carbon dioxide. This is the hydrogen Australia is focused on producing and exporting.",
    image: 'https://readdy.ai/api/search-image?query=Large%20industrial%20electrolyser%20stacks%20for%20green%20hydrogen%20production%20inside%20modern%20facility%2C%20blue%20and%20green%20hydrogen%20gas%20pipes%2C%20clean%20technology%20industrial%20photography%2C%20advanced%20renewable%20energy%20infrastructure%2C%20bright%20clean%20environment&width=700&height=480&seq=h2-green-2026&orientation=landscape',
    specs: [
      { label: 'Production method', value: 'Water electrolysis' },
      { label: 'Energy source', value: 'Solar, wind, hydro' },
      { label: 'CO₂ emissions', value: '0 kg CO₂/kg H₂' },
      { label: 'Current cost (AU)', value: '$5–$8/kg' },
      { label: '2030 cost target', value: '$2/kg (H2X)' },
      { label: 'Australia focus', value: 'Export + domestic' },
    ],
    highlight: "Australia's primary hydrogen focus",
  },
  {
    colour: 'Blue',
    badge: 'Blue Hydrogen',
    badgeStyle: 'bg-secondary-100 text-secondary-700',
    borderStyle: 'border-secondary-200',
    icon: 'ri-drop-line',
    iconBg: 'bg-secondary-50',
    iconColor: 'text-secondary-600',
    tagline: 'Fossil-derived but with carbon capture — a low-carbon bridge',
    description: 'Blue hydrogen is produced from natural gas (steam methane reforming) with the carbon dioxide captured and stored underground (CCS). It produces significantly lower emissions than conventional grey hydrogen. Some Australian projects are exploring blue hydrogen as a near-term export product while green hydrogen costs decline.',
    image: 'https://readdy.ai/api/search-image?query=Natural%20gas%20processing%20facility%20with%20carbon%20capture%20storage%20infrastructure%2C%20industrial%20plant%20with%20blue%20tones%2C%20CCS%20pipes%20and%20injection%20equipment%2C%20professional%20energy%20infrastructure%20photography%2C%20Australian%20industrial%20setting&width=700&height=480&seq=h2-blue-2026&orientation=landscape',
    specs: [
      { label: 'Production method', value: 'Steam methane reforming' },
      { label: 'Energy source', value: 'Natural gas + CCS' },
      { label: 'CO₂ emissions', value: '0.5–4 kg CO₂/kg H₂' },
      { label: 'Current cost (AU)', value: '$2–$3/kg' },
      { label: 'CCS capture rate', value: '85–95%' },
      { label: 'Australia role', value: 'Bridge technology' },
    ],
    highlight: 'Near-term low-carbon bridge',
  },
  {
    colour: 'Grey',
    badge: 'Grey Hydrogen',
    badgeStyle: 'bg-background-200 text-foreground-600',
    borderStyle: 'border-background-300',
    icon: 'ri-building-line',
    iconBg: 'bg-background-100',
    iconColor: 'text-foreground-500',
    tagline: 'Conventional hydrogen — the industry baseline being replaced',
    description: "Grey hydrogen is the most common form today, produced from natural gas without carbon capture. It accounts for roughly 95% of global hydrogen production. Grey hydrogen is not considered a sustainable energy solution — it emits 9–12 kg of CO₂ per kg of H₂ produced. The transition to green hydrogen aims to replace grey hydrogen across all industrial applications.",
    image: 'https://readdy.ai/api/search-image?query=Conventional%20industrial%20steam%20methane%20reforming%20plant%2C%20large%20chemical%20processing%20facility%2C%20grey%20and%20industrial%20tones%2C%20traditional%20energy%20infrastructure%2C%20professional%20industrial%20photography%2C%20contrast%20with%20clean%20energy&width=700&height=480&seq=h2-grey-2026&orientation=landscape',
    specs: [
      { label: 'Production method', value: 'Steam methane reforming' },
      { label: 'Energy source', value: 'Natural gas (no CCS)' },
      { label: 'CO₂ emissions', value: '9–12 kg CO₂/kg H₂' },
      { label: 'Current cost', value: '$1–$2/kg' },
      { label: 'Sustainability', value: 'Not sustainable' },
      { label: 'Future role', value: 'Being phased out' },
    ],
    highlight: 'Being replaced by green hydrogen',
  },
];

export default function HydrogenTypes() {
  return (
    <section className="bg-background-100/50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/50 mb-5">
            <i className="ri-palette-line text-primary-500 text-sm"></i>
            <span className="text-xs font-semibold text-primary-700 tracking-wide uppercase whitespace-nowrap">Types of Hydrogen</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
            Green, Blue &amp; Grey:<br className="hidden md:block" /> Understanding the Hydrogen Colour Spectrum
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Not all hydrogen is equal. The &ldquo;colour&rdquo; of hydrogen describes how it was made and how much carbon was emitted in the process. Australia is focused on green hydrogen.
          </p>
        </div>

        <div className="space-y-10 md:space-y-14">
          {types.map((type, idx) => (
            <article
              key={type.colour}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${idx % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
            >
              {/* Image */}
              <div className={`${idx % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className={`relative rounded-2xl overflow-hidden border-2 ${type.borderStyle}`}>
                  <img
                    src={type.image}
                    alt={`${type.badge} production — AK Energy hydrogen solutions`}
                    className="w-full h-72 md:h-80 object-cover object-top"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${type.badgeStyle}`}>
                      {type.badge}
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2 max-w-[180px]">
                    <span className="text-xs font-bold text-foreground-700">{type.highlight}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`${idx % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div className={`w-12 h-12 rounded-xl ${type.iconBg} flex items-center justify-center mb-4`}>
                  <i className={`${type.icon} text-xl ${type.iconColor}`}></i>
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 mb-2">{type.badge}</h3>
                <p className="text-sm font-semibold text-foreground-500 italic mb-4">&ldquo;{type.tagline}&rdquo;</p>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">{type.description}</p>

                {/* Spec grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {type.specs.map((spec) => (
                    <div key={spec.label} className="bg-background-50 rounded-xl p-3 border border-background-200/70">
                      <p className="text-xs text-foreground-500 mb-1">{spec.label}</p>
                      <p className="text-sm font-semibold text-foreground-900">{spec.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}